// src/modules/quantum/QuantumCircuits.jsx
// Quantum Circuits — gates · circuit builder · entanglement · interference · algorithms

import React, { useState, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#22d3ee';
import { _livePalette as P } from '../../context/paletteSync';
const PI = Math.PI;

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);

// ─── QUANTUM STATE SIMULATOR (2-qubit, exact) ─────────────────────────────────
// State vector: [amp_00, amp_01, amp_10, amp_11] (complex as {re,im})
const zero = () => ({re:0,im:0});
const c = (re,im=0) => ({re,im});
const cadd = (a,b) => ({re:a.re+b.re, im:a.im+b.im});
const cmul = (a,b) => ({re:a.re*b.re-a.im*b.im, im:a.re*b.im+a.im*b.re});
const cabs2 = a => a.re**2+a.im**2;
const INV2 = 1/Math.sqrt(2);

const initState = (n=2) => {
  const s = Array.from({length:2**n},(_,i)=>i===0?c(1):c(0));
  return s;
};

// Apply single-qubit gate to qubit q of n-qubit state
const applyGate1 = (state, gate, q, n=2) => {
  const N = 2**n;
  const res = state.map(()=>c(0));
  for(let i=0;i<N;i++){
    const bit = (i>>>(n-1-q))&1;
    const partner = i^(1<<(n-1-q));
    let a,b;
    if(bit===0){ a=state[i]; b=state[partner]; }
    else { a=state[partner]; b=state[i]; }
    let outA, outB;
    switch(gate){
      case 'H': outA=cmul(c(INV2),cadd(a,b)); outB=cmul(c(INV2),cadd(a,c(-b.re,-b.im))); break;
      case 'X': outA=b; outB=a; break;
      case 'Y': outA=cmul(c(0,1),b); outB=cmul(c(0,-1),a); break;
      case 'Z': outA=a; outB=cmul(c(-1),b); break;
      case 'S': outA=a; outB=cmul(c(0,1),b); break;
      case 'T': outA=a; outB=cmul({re:INV2,im:INV2},b); break;
      default:  outA=a; outB=b;
    }
    if(bit===0){ res[i]=cadd(res[i],outA); res[partner]=cadd(res[partner],outB); }
    else { res[partner]=cadd(res[partner],outA); res[i]=cadd(res[i],outB); }
  }
  return res;
};

// CNOT: control=c, target=t
const applyCNOT = (state, ctrl, tgt, n=2) => {
  const N=2**n;
  return state.map((amp,i)=>{
    const cb=(i>>>(n-1-ctrl))&1;
    const tb=(i>>>(n-1-tgt))&1;
    if(cb===1&&tb===0){ const j=i^(1<<(n-1-tgt)); return state[j]; }
    if(cb===1&&tb===1){ const j=i^(1<<(n-1-tgt)); return state[j]; }
    return amp;
  });
};

// Get probabilities from state vector
const getProbs = (state, labels) =>
  state.map((a,i)=>({label:labels[i], prob:+cabs2(a).toFixed(5), re:+a.re.toFixed(4), im:+a.im.toFixed(4)}));

const stateLabels2 = ['|00⟩','|01⟩','|10⟩','|11⟩'];

// ─── CIRCUIT BUILDER SVG ─────────────────────────────────────────────────────
const GATE_COLORS = {H:'#fbbf24',X:'#f87171',Y:'#fb923c',Z:'#22d3ee',S:'#4ade80',T:'#a78bfa',CNOT:'#22d3ee',SWAP:'#4ade80','M':'#64748b'};

function CircuitSVG({ circuit, nQubits=2, onClickCell }) {
  const W=680, H=nQubits*70+40, qH=70, wireY=q=>40+q*qH;
  const colW=60, startX=80;

  const maxCols = Math.max(8, circuit.reduce((m,g)=>Math.max(m,g.col+1),0)+2);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Qubit labels */}
      {Array.from({length:nQubits},(_,q)=>(
        <g key={q}>
          <text x={12} y={wireY(q)+4} style={{fontSize:'11px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>q{q}</text>
          <text x={38} y={wireY(q)+4} style={{fontSize:'10px',fill:'#334155',fontFamily:'JetBrains Mono'}}>|0⟩</text>
          {/* Wire */}
          <line x1={startX} y1={wireY(q)} x2={W-20} y2={wireY(q)} stroke="#1e293b" strokeWidth={2}/>
        </g>
      ))}

      {/* Gate cells (click to add) */}
      {Array.from({length:maxCols},(_,col)=>
        Array.from({length:nQubits},(_,q)=>{
          const gate = circuit.find(g=>g.col===col&&g.qubit===q);
          const x = startX+col*colW, y = wireY(q);
          const color = gate ? (GATE_COLORS[gate.type]||'#64748b') : '#ffffff08';
          return (
            <g key={`${col}-${q}`} onClick={()=>onClickCell&&onClickCell(col,q)} style={{cursor:'pointer'}}>
              <rect x={x-18} y={y-18} width={36} height={36} rx={4}
                fill={gate?color+'28':'#ffffff04'} stroke={gate?color:'#1e293b'}
                strokeWidth={gate?1.5:0.5}/>
              {gate&&<text x={x} y={y+5} textAnchor="middle" style={{fontSize:'11px',fill:color,fontFamily:'JetBrains Mono',fontWeight:700,pointerEvents:'none'}}>{gate.type==='CNOT'?'●':gate.type==='M'?'⊙':gate.type}</text>}
              {gate?.type==='CNOT'&&<line x1={x} y1={y+18} x2={x} y2={wireY(gate.target)-18} stroke={GATE_COLORS.CNOT} strokeWidth={1.5}/>}
              {gate?.type==='CNOT'&&<circle cx={x} cy={wireY(gate.target)} r={10} fill="none" stroke={GATE_COLORS.CNOT} strokeWidth={1.5}/>}
              {gate?.type==='CNOT'&&<line x1={x-10} y1={wireY(gate.target)} x2={x+10} y2={wireY(gate.target)} stroke={GATE_COLORS.CNOT} strokeWidth={1.5}/>}
            </g>
          );
        })
      )}
    </svg>
  );
}

// ─── PRESET CIRCUITS ─────────────────────────────────────────────────────────
const PRESETS = {
  bell: {
    name:'Bell State |Φ⁺⟩',
    desc:'H on q0, then CNOT(0→1). Creates maximally entangled pair.',
    gates:[{type:'H',qubit:0,col:0},{type:'CNOT',qubit:0,target:1,col:1}],
    simulate:(s)=>{ s=applyGate1(s,'H',0); s=applyCNOT(s,0,1); return s; }
  },
  ghz: {
    name:'GHZ State (2q)',
    desc:'H on q0, CNOT(0→1). 2-qubit GHZ: (|00⟩+|11⟩)/√2.',
    gates:[{type:'H',qubit:0,col:0},{type:'CNOT',qubit:0,target:1,col:1}],
    simulate:(s)=>{ s=applyGate1(s,'H',0); s=applyCNOT(s,0,1); return s; }
  },
  teleport: {
    name:'Quantum Teleportation (prep)',
    desc:'Create Bell pair + encode qubit. Step 1 of teleportation protocol.',
    gates:[{type:'H',qubit:0,col:0},{type:'CNOT',qubit:0,target:1,col:1},{type:'H',qubit:0,col:2},{type:'M',qubit:0,col:3},{type:'M',qubit:1,col:3}],
    simulate:(s)=>{ s=applyGate1(s,'H',0); s=applyCNOT(s,0,1); s=applyGate1(s,'H',0); return s; }
  },
  superpos: {
    name:'Full Superposition',
    desc:'H on both qubits → equal superposition of all 4 states.',
    gates:[{type:'H',qubit:0,col:0},{type:'H',qubit:1,col:0}],
    simulate:(s)=>{ s=applyGate1(s,'H',0); s=applyGate1(s,'H',1); return s; }
  },
  interference: {
    name:'Interference (H·H)',
    desc:'H then H → identity. Constructive + destructive interference cancel perfectly.',
    gates:[{type:'H',qubit:0,col:0},{type:'H',qubit:0,col:1}],
    simulate:(s)=>{ s=applyGate1(s,'H',0); s=applyGate1(s,'H',0); return s; }
  },
};

const GATE_PALETTE = ['H','X','Y','Z','S','T'];

// ─── GATE REFERENCE ──────────────────────────────────────────────────────────
const GATE_DEFS = [
  {name:'H (Hadamard)',matrix:'[[1,1],[1,-1]]/√2',effect:'|0⟩→|+⟩, |1⟩→|−⟩. Creates superposition.',color:'#fbbf24'},
  {name:'X (NOT/Pauli-X)',matrix:'[[0,1],[1,0]]',effect:'|0⟩↔|1⟩. Quantum NOT. 180° rotation around x-axis.',color:'#f87171'},
  {name:'Y (Pauli-Y)',matrix:'[[0,-i],[i,0]]',effect:'|0⟩→i|1⟩, |1⟩→-i|0⟩. 180° rotation around y-axis.',color:'#fb923c'},
  {name:'Z (Pauli-Z)',matrix:'[[1,0],[0,-1]]',effect:'Phase flip. |1⟩→-|1⟩. 180° around z-axis.',color:'#22d3ee'},
  {name:'S (Phase)',matrix:'[[1,0],[0,i]]',effect:'|1⟩→i|1⟩. 90° phase rotation around z-axis.',color:'#4ade80'},
  {name:'T (π/8)',matrix:'[[1,0],[0,e^(iπ/4)]]',effect:'|1⟩→e^(iπ/4)|1⟩. 45° phase. Universal gate set.',color:'#a78bfa'},
  {name:'CNOT',matrix:'[[1,0,0,0],[0,1,0,0],[0,0,0,1],[0,0,1,0]]',effect:'Flips target qubit if control=|1⟩. Creates entanglement.',color:'#22d3ee'},
  {name:'Toffoli (CCX)',matrix:'3-qubit gate',effect:'Flips target if both controls=|1⟩. Universal classical computation.',color:'#64748b'},
];

const TABS = ['builder','gates','interference','entanglement','algorithms'];

export default function QuantumCircuits() {
  const [tab, setTab] = useState('builder');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Quantum Circuits — build gates, observe superposition, create entanglement.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [preset, setPreset] = useState('bell');
  const [circuit, setCircuit] = useState(PRESETS.bell.gates);
  const [state, setState] = useState(null);
  const [selectedGate, setSelectedGate] = useState('H');
  const [probs, setProbs] = useState(null);

  const simulate = useCallback(() => {
    const p = PRESETS[preset];
    if(!p) return;
    let s = initState(2);
    s = p.simulate(s);
    setState(s);
    const ps = getProbs(s, stateLabels2);
    setProbs(ps);
    log(`Simulated ${p.name} → ${ps.filter(p=>p.prob>0.001).map(p=>`${p.label}:${(p.prob*100).toFixed(1)}%`).join(', ')}`,'ok');
  }, [preset]);

  const loadPreset = (key) => {
    setPreset(key);
    setCircuit(PRESETS[key].gates);
    setState(null);
    setProbs(null);
  };

  // Interference data
  const interferenceData = [
    {label:'|0⟩ after H',     p00:0.5,p01:0.5,desc:'Equal superposition'},
    {label:'|0⟩ after H·H',   p00:1.0,p01:0.0,desc:'Constructive → |0⟩'},
    {label:'|1⟩ after H·H',   p00:0.0,p01:1.0,desc:'Destructive → |1⟩'},
    {label:'After H·Z·H',     p00:0.0,p01:1.0,desc:'Z conjugated by H = X'},
  ];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── BUILDER ── */}
      {tab==='builder'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'200px 1fr',gap:'12px'}}>
            <Panel title="PRESET CIRCUITS" accent={AC}>
              {Object.entries(PRESETS).map(([k,v])=>(
                <button key={k} onClick={()=>loadPreset(k)} style={{display:'block',width:'100%',padding:'8px',background:preset===k?AC+'18':'var(--deep)',border:`1px solid ${preset===k?AC:'var(--b0)'}`,color:preset===k?AC:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',marginBottom:'4px',fontFamily:'JetBrains Mono',textAlign:'left',fontWeight:preset===k?700:400}}>
                  {v.name}<br/><span style={{fontWeight:400,color:'var(--t3)',fontSize:'8px'}}>{v.desc.slice(0,44)}</span>
                </button>
              ))}
              <Btn onClick={simulate} color="#4ade80" style={{width:'100%',marginTop:'4px'}}>SIMULATE →</Btn>
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <Panel title={`CIRCUIT: ${PRESETS[preset]?.name}`} accent={AC}>
                <CircuitSVG circuit={circuit} nQubits={2}/>
                <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'6px'}}>{PRESETS[preset]?.desc}</div>
              </Panel>
              {probs&&(
                <Panel title="STATE VECTOR — MEASUREMENT PROBABILITIES" accent="#4ade80">
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={probs} barSize={40}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="label" tick={{fontSize:11,fill:'var(--t1)',fontFamily:'JetBrains Mono',fontWeight:700}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                        <Tooltip/>
                        <Bar dataKey="prob" name="Probability" radius={[3,3,0,0]}>
                          {probs.map((_,i)=><Cell key={i} fill={P[i%P.length]}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
                      {probs.map((p,i)=>(
                        <div key={p.label} style={{padding:'6px 10px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${P[i%P.length]}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <span style={{fontSize:'11px',color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700}}>{p.label}</span>
                          <span style={{fontSize:'9px',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{(p.prob*100).toFixed(1)}%</span>
                          <span style={{fontSize:'8px',color:'#64748b',fontFamily:'JetBrains Mono'}}>{p.re}+{p.im}i</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Panel>
              )}
            </div>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {/* ── GATES ── */}
      {tab==='gates'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {GATE_DEFS.map(g=>(
              <Panel key={g.name} title={g.name} accent={g.color}>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'9px',color:g.color,padding:'6px',background:'var(--deep)',borderRadius:'2px',marginBottom:'6px'}}>{g.matrix}</div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{g.effect}</div>
              </Panel>
            ))}
          </div>
          <Panel title="UNIVERSALITY — WHICH GATES FORM A UNIVERSAL SET?" accent="#fbbf24">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {set:'{H, T, CNOT}',univ:true,desc:'Standard universal gate set. Any unitary can be approximated with these 3 gate types. Basis of most quantum computers.',color:'#4ade80'},
                {set:'{Rx, Ry, CNOT}',univ:true,desc:'Continuous rotation gates + entangling gate. Used in variational circuits (VQE, QAOA). Parameterized.',color:'#22d3ee'},
                {set:'{X, Z, CNOT}',univ:false,desc:'NOT universal — can only produce states reachable from stabilizer group. Efficiently simulable classically (Gottesman-Knill).',color:'#f87171'},
              ].map(({set,univ,desc,color})=>(
                <div key={set} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'3px'}}>{set}</div>
                  <div style={{fontSize:'9px',color:univ?'#4ade80':'#f87171',marginBottom:'5px',fontWeight:700}}>{univ?'✓ Universal':'✗ Not Universal'}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── INTERFERENCE ── */}
      {tab==='interference'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="CONSTRUCTIVE INTERFERENCE — H·H = I" accent="#fbbf24">
              <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Apply H twice: amplitude paths add constructively for |0⟩ and cancel for |1⟩.
              </div>
              {[
                {path:'|0⟩→|+⟩→',amp:'(+1/√2)(+1/√2) = +1/2',color:'#4ade80',state:'|0⟩'},
                {path:'|0⟩→|+⟩→',amp:'(+1/√2)(−1/√2) = −1/2',color:'#4ade80',state:'|0⟩'},
                {path:'Sum for |0⟩:',amp:'+1/2 + 1/2 = 1 ✓',color:'#4ade80',state:'→|0⟩ CONSTRUCTIVE'},
                {path:'Sum for |1⟩:',amp:'+1/2 − 1/2 = 0',color:'#f87171',state:'→ 0 DESTRUCTIVE'},
              ].map((r,i)=>(
                <div key={i} style={{display:'flex',gap:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:`2px solid ${r.color}`}}>
                  <span style={{fontSize:'9px',color:'var(--t3)',minWidth:130,fontFamily:'JetBrains Mono'}}>{r.path}</span>
                  <span style={{fontSize:'9px',color:r.color,fontFamily:'JetBrains Mono',flex:1}}>{r.amp}</span>
                </div>
              ))}
              <div style={{marginTop:'8px',padding:'8px',background:'#fbbf2410',borderRadius:'3px',border:'1px solid #fbbf2430',fontSize:'9px',color:'#fbbf24',lineHeight:1.7}}>
                <span style={{fontWeight:700}}>Key insight: </span>Quantum algorithms work by engineering interference patterns — amplifying correct answer paths, cancelling wrong ones.
              </div>
            </Panel>
            <Panel title="DEUTSCH-JOZSA — FIRST QUANTUM SPEEDUP" accent="#a78bfa">
              <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Is a black-box function f(x) constant or balanced? Classical: O(2^(n-1)+1) calls. Quantum: 1 call.
              </div>
              {[
                {step:'1. Init |0⟩⊗n|1⟩',desc:'Input register in |0⟩, ancilla in |1⟩'},
                {step:'2. H⊗(n+1)',desc:'All qubits to superposition — query all inputs at once'},
                {step:'3. Apply Uf',desc:'Oracle: |x⟩|y⟩ → |x⟩|y⊕f(x)⟩. Phase kickback encodes f.'},
                {step:'4. H⊗n on input',desc:'Interference: constant f → all |0⟩, balanced → ≠all |0⟩'},
                {step:'5. Measure',desc:'Constant: measure |00…0⟩. Balanced: anything else.'},
              ].map(({step,desc},i)=>(
                <div key={i} style={{display:'flex',gap:'10px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px'}}>
                  <span style={{fontSize:'9px',color:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:120}}>{step}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{desc}</span>
                </div>
              ))}
            </Panel>
          </div>
          <Panel title="PHASE KICKBACK — THE MECHANISM BEHIND MOST QUANTUM ALGORITHMS" accent="#4ade80">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <div style={{fontSize:'10px',color:'#4ade80',fontWeight:700,marginBottom:'8px'}}>How Phase Kickback Works</div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>When oracle Uf acts on |x⟩|−⟩, the phase from the ancilla "kicks back" into the control register:</div>
                <div style={{padding:'10px',background:'var(--deep)',borderRadius:'4px',fontFamily:'JetBrains Mono',fontSize:'10px',color:'#4ade80',lineHeight:2}}>
                  Uf|x⟩|−⟩ = (−1)^f(x) |x⟩|−⟩
                </div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginTop:'8px'}}>The ancilla returns unchanged. The phase (−1)^f(x) is now encoded in the control qubit amplitude. This is how quantum algorithms encode information without directly measuring it.</div>
              </div>
              <div>
                <div style={{fontSize:'10px',color:'#22d3ee',fontWeight:700,marginBottom:'8px'}}>Algorithms Using Kickback</div>
                {[
                  {alg:'Deutsch-Jozsa',use:'Distinguish constant vs balanced function'},
                  {alg:'Bernstein-Vazirani',use:'Find hidden string in 1 query vs n'},
                  {alg:'Simon\'s Algorithm',use:'Find hidden period, exponential speedup'},
                  {alg:'Shor\'s Algorithm',use:'Period finding → integer factoring'},
                  {alg:'Grover\'s Algorithm',use:'Phase oracle marks correct answer'},
                  {alg:'HHL Algorithm',use:'Linear systems solving'},
                ].map(({alg,use})=>(
                  <div key={alg} style={{display:'flex',gap:'8px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #22d3ee44'}}>
                    <span style={{fontSize:'9px',color:'#22d3ee',fontWeight:700,minWidth:130}}>{alg}</span>
                    <span style={{fontSize:'9px',color:'var(--t3)'}}>{use}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── ENTANGLEMENT ── */}
      {tab==='entanglement'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="ENTANGLEMENT SIMULATION — BELL PAIR CORRELATIONS" accent="#4ade80">
            {(()=>{
              let s = initState(2);
              s = applyGate1(s,'H',0);
              s = applyCNOT(s,0,1);
              const p = getProbs(s,stateLabels2);
              const shots = 400;
              const results = Array.from({length:shots},()=>Math.random()<0.5?[0,0]:[1,1]);
              const corrData = [
                {name:'Both 0',count:results.filter(r=>r[0]===0&&r[1]===0).length,color:'#4ade80'},
                {name:'0,1',count:results.filter(r=>r[0]===0&&r[1]===1).length,color:'#f87171'},
                {name:'1,0',count:results.filter(r=>r[0]===1&&r[1]===0).length,color:'#f87171'},
                {name:'Both 1',count:results.filter(r=>r[0]===1&&r[1]===1).length,color:'#4ade80'},
              ];
              return (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                  <div>
                    <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                      |Φ⁺⟩ = (|00⟩+|11⟩)/√2<br/>
                      Measuring q0 instantly determines q1 — always same result. Never mixed results.
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={corrData} barSize={50}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="name" tick={{fontSize:10,fill:'var(--t1)',fontFamily:'JetBrains Mono'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Bar dataKey="count" radius={[3,3,0,0]}>
                          {corrData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    {p.map((ps,i)=>(
                      <div key={ps.label} style={{display:'flex',alignItems:'center',gap:'8px',padding:'10px',background:'var(--deep)',borderRadius:'3px',marginBottom:'5px',borderLeft:`3px solid ${P[i]}`}}>
                        <span style={{fontSize:'14px',color:P[i],fontFamily:'JetBrains Mono',fontWeight:700,minWidth:40}}>{ps.label}</span>
                        <div style={{flex:1,height:14,background:'var(--b0)',borderRadius:2,overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${ps.prob*100}%`,background:P[i],borderRadius:2,transition:'width .3s'}}/>
                        </div>
                        <span style={{fontSize:'10px',color:P[i],fontFamily:'JetBrains Mono',fontWeight:700,minWidth:40}}>{(ps.prob*100).toFixed(1)}%</span>
                      </div>
                    ))}
                    <div style={{padding:'8px',background:'#4ade8010',borderRadius:'3px',border:'1px solid #4ade8030',fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginTop:'4px'}}>
                      <span style={{color:'#4ade80',fontWeight:700}}>Spooky action: </span>
                      The correlation is non-local — no classical hidden variable can explain it. Bell inequality violation proven experimentally (Nobel Prize 2022).
                    </div>
                  </div>
                </div>
              );
            })()}
          </Panel>
          <Panel title="QUANTUM TELEPORTATION PROTOCOL" accent="#a78bfa">
            <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
              {[
                {step:'Alice has |ψ⟩ to send',detail:'|ψ⟩ = α|0⟩+β|1⟩. She wants Bob to have this state.',color:'#a78bfa'},
                {step:'Prepare Bell pair',detail:'Create |Φ⁺⟩ shared between Alice (q1) and Bob (q2). H(q1) + CNOT(1→2).',color:'#22d3ee'},
                {step:'Alice applies CNOT(ψ→q1)',detail:'Entangles |ψ⟩ with her half of Bell pair.',color:'#4ade80'},
                {step:'Alice applies H on |ψ⟩',detail:'Rotates state into measurement basis.',color:'#fbbf24'},
                {step:'Alice measures both qubits',detail:'Gets 2 classical bits (00, 01, 10, or 11). |ψ⟩ is now destroyed.',color:'#fb923c'},
                {step:'Alice sends 2 bits to Bob',detail:'Classical channel — this limits teleportation to light speed.',color:'#f87171'},
                {step:'Bob applies correction gates',detail:'00→I, 01→X, 10→Z, 11→ZX on q2. His qubit is now |ψ⟩.',color:'#4ade80'},
              ].map(({step,detail,color},i)=>(
                <div key={i} style={{display:'flex',gap:'10px',padding:'7px 10px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${color}`}}>
                  <span style={{fontSize:'9px',color:color,fontFamily:'JetBrains Mono',fontWeight:700,minWidth:160}}>{step}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── ALGORITHMS ── */}
      {tab==='algorithms'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            {[
              {name:"Grover's Search",speedup:'O(√N) vs O(N)',color:'#4ade80',steps:['Initialize equal superposition over N states','Apply phase oracle: marks target state with −1 phase','Apply diffusion operator: inversion about mean','Repeat √N times — amplitude of target grows','Measure — find target with high probability'],use:'Searching unsorted databases, NP-hard optimization'},
              {name:"Shor's Factoring",speedup:'O(log³N) vs O(e^(N^1/3))',color:'#f87171',steps:['Choose random a < N','Find period r of f(x)=aˣ mod N via QFT','If r even and a^(r/2)≠±1 mod N...','gcd(a^(r/2)±1, N) gives non-trivial factor','Repeat if unlucky'],use:'Breaking RSA encryption, number theory'},
              {name:'QFT (Quantum Fourier)',speedup:'O(n²) vs O(n·2ⁿ)',color:'#22d3ee',steps:['Apply H to first qubit','Apply controlled phase rotations from subsequent qubits','Move to next qubit, repeat','Reverse qubit order at end','Exact DFT on quantum amplitudes'],use:'Core subroutine in Shor, HHL, phase estimation'},
              {name:'VQE (Variational)',speedup:'Heuristic, NISQ-friendly',color:'#a78bfa',steps:['Prepare parameterized ansatz state |ψ(θ)⟩','Measure expectation value ⟨H⟩ on quantum computer','Classical optimizer updates θ to minimize ⟨H⟩','Repeat until convergence to ground state energy','Hybrid quantum-classical loop'],use:'Quantum chemistry, drug discovery, materials'},
            ].map(a=>(
              <Panel key={a.name} title={a.name} accent={a.color}>
                <div style={{padding:'5px 8px',background:`${a.color}18`,borderRadius:'2px',marginBottom:'8px',border:`1px solid ${a.color}33`}}>
                  <span style={{fontSize:'9px',color:a.color,fontFamily:'JetBrains Mono',fontWeight:700}}>Speedup: {a.speedup}</span>
                </div>
                {a.steps.map((s,i)=>(
                  <div key={i} style={{display:'flex',gap:'8px',marginBottom:'3px',padding:'4px 6px',background:'var(--deep)',borderRadius:'2px'}}>
                    <span style={{fontSize:'9px',color:a.color,fontFamily:'JetBrains Mono',fontWeight:700,minWidth:14}}>{i+1}.</span>
                    <span style={{fontSize:'9px',color:'var(--t3)'}}>{s}</span>
                  </div>
                ))}
                <div style={{marginTop:'6px',fontSize:'9px',color:'var(--t2)',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${a.color}`}}>
                  <span style={{color:a.color}}>Use: </span>{a.use}
                </div>
              </Panel>
            ))}
          </div>
          <Panel title="QUANTUM SPEEDUP LANDSCAPE" accent="#fbbf24">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Algorithm','Problem','Classical','Quantum','Speedup type'].map(h=><th key={h} style={{padding:'6px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ["Shor's","Integer factoring","O(exp(n^1/3))","O(n³)","Exponential"],
                  ["Grover's","Unstructured search","O(N)","O(√N)","Quadratic"],
                  ["HHL","Linear systems","O(Ns·κ²/ε)","O(log(N)κ²/ε)","Exponential*"],
                  ["QML Kernel","Kernel methods","O(N²d)","O(log(Nd))","Exponential*"],
                  ["QAOA","Combinatorial opt","NP-hard","Heuristic","Unknown"],
                  ["VQE","Ground state energy","Exp classical","Polynomial*","Polynomial*"],
                ].map((row,i)=>(
                  <tr key={row[0]} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    {row.map((cell,j)=>(
                      <td key={j} style={{padding:'6px 10px',color:j===4?['#4ade80','#22d3ee','#4ade80','#4ade80','#fbbf24','#fbbf24'][i]:j===0?P[i%P.length]:'var(--t3)',fontFamily:j>0?'JetBrains Mono':'inherit',fontWeight:j===0?700:400,fontSize:'9px'}}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{fontSize:'8px',color:'#64748b',marginTop:'6px'}}>* Caveats apply — dequantization results show some speedups may be smaller than claimed</div>
          </Panel>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
